import React, { useMemo } from "react";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
  SelectChangeEvent,
} from "@mui/material";
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  FirstPage as FirstPageIcon,
  LastPage as LastPageIcon,
} from "@mui/icons-material";

export interface PaginationControlsProps {
  currentPage: number;
  totalPages: number;
  rowsPerPage: number;
  totalItems: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (rowsPerPage: number) => void;
  rowsPerPageOptions?: number[];
}

const PaginationControls: React.FC<PaginationControlsProps> = ({
  currentPage,
  totalPages,
  rowsPerPage,
  totalItems,
  onPageChange,
  onRowsPerPageChange,
  rowsPerPageOptions = [5, 10, 25, 50],
}) => {
  const pageNumbers = useMemo(() => {
    const pages: (number | string)[] = [];
    const maxVisiblePages = 5;

    if (totalPages <= 0) {
      return pages;
    }

    if (totalPages <= maxVisiblePages + 2) {
      for (let i = 0; i < totalPages; i++) {
        pages.push(i);
      }
      return pages;
    }

    pages.push(0);

    if (currentPage > 2) {
      pages.push("...");
    }

    const start = Math.max(1, currentPage - 1);
    const end = Math.min(totalPages - 2, currentPage + 1);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (currentPage < totalPages - 3) {
      pages.push("...");
    }

    pages.push(totalPages - 1);
    return pages;
  }, [currentPage, totalPages]);

  const displayFrom =
    totalItems === 0 ? 0 : currentPage * rowsPerPage + 1;
  const displayTo = Math.min((currentPage + 1) * rowsPerPage, totalItems);
  const safeTotalPages = totalPages || 1;
  const disableBack = currentPage === 0 || totalPages <= 1;
  const disableForward =
    currentPage >= totalPages - 1 || totalPages <= 1;

  const handleRowsPerPageChange = (event: SelectChangeEvent<number>) => {
    onRowsPerPageChange(Number(event.target.value));
  };

  const handlePageChange = (page: number) => {
    const nextPage = Math.max(0, Math.min(page, Math.max(totalPages - 1, 0)));
    onPageChange(nextPage);
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        alignItems: { xs: "stretch", md: "center" },
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 2,
      }}
    >
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          textAlign: { xs: "center", md: "left" },
          order: { xs: 2, md: 1 },
        }}
      >
        Showing{" "}
        <Box component="span" fontWeight="bold" color="text.primary">
          {displayFrom}
        </Box>
        {" - "}
        <Box component="span" fontWeight="bold" color="text.primary">
          {displayTo}
        </Box>
        {" of "}
        <Box component="span" fontWeight="bold" color="text.primary">
          {totalItems}
        </Box>
      </Typography>

      <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
        justifyContent="center"
        sx={{ order: { xs: 1, md: 2 } }}
      >
        <Tooltip title="First page">
          <span>
            <IconButton
              onClick={() => handlePageChange(0)}
              disabled={disableBack}
              size="small"
              sx={{
                border: "1px solid",
                borderColor: "grey.400",
                borderRadius: 1,
                "&:hover": {
                  bgcolor: "action.hover",
                },
                "&.Mui-disabled": {
                  opacity: 0.5,
                },
              }}
            >
              <FirstPageIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Previous page">
          <span>
            <IconButton
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={disableBack}
              size="small"
              sx={{
                border: "1px solid",
                borderColor: "grey.400",
                borderRadius: 1,
                "&:hover": {
                  bgcolor: "action.hover",
                },
                "&.Mui-disabled": {
                  opacity: 0.5,
                },
              }}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Stack
          direction="row"
          spacing={0.5}
          sx={{ display: { xs: "none", sm: "flex" } }}
        >
          {pageNumbers.map((pageNum, index) =>
            pageNum === "..." ? (
              <Typography
                key={`ellipsis-${index}`}
                variant="body2"
                sx={{ px: 1, alignSelf: "center" }}
                color="text.secondary"
              >
                ...
              </Typography>
            ) : (
              <Button
                key={pageNum}
                onClick={() => handlePageChange(pageNum as number)}
                variant={currentPage === pageNum ? "contained" : "outlined"}
                size="small"
                sx={{
                  minWidth: 36,
                  height: 36,
                  p: 0,
                  borderRadius: 1,
                  fontWeight: currentPage === pageNum ? "bold" : "normal",
                  ...(currentPage !== pageNum && {
                    borderColor: "grey.400",
                    color: "text.primary",
                    "&:hover": {
                      bgcolor: "action.hover",
                      borderColor: "primary.main",
                    },
                  }),
                }}
              >
                {(pageNum as number) + 1}
              </Button>
            )
          )}
        </Stack>

        <Typography
          variant="body2"
          sx={{
            display: { xs: "block", sm: "none" },
            px: 2,
            fontWeight: "medium",
          }}
        >
          {currentPage + 1} / {safeTotalPages}
        </Typography>

        <Tooltip title="Next page">
          <span>
            <IconButton
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={disableForward}
              size="small"
              sx={{
                border: "1px solid",
                borderColor: "grey.400",
                borderRadius: 1,
                "&:hover": {
                  bgcolor: "action.hover",
                },
                "&.Mui-disabled": {
                  opacity: 0.5,
                },
              }}
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>

        <Tooltip title="Last page">
          <span>
            <IconButton
              onClick={() => handlePageChange(totalPages - 1)}
              disabled={disableForward}
              size="small"
              sx={{
                border: "1px solid",
                borderColor: "grey.400",
                borderRadius: 1,
                "&:hover": {
                  bgcolor: "action.hover",
                },
                "&.Mui-disabled": {
                  opacity: 0.5,
                },
              }}
            >
              <LastPageIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </Stack>

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent={{ xs: "center", md: "flex-end" }}
        sx={{ order: 3 }}
      >
        <Typography variant="body2" color="text.secondary">
          Per page:
        </Typography>
        <FormControl size="small">
          <Select
            value={rowsPerPage}
            onChange={handleRowsPerPageChange}
            sx={{
              minWidth: 70,
              height: 36,
              "& .MuiSelect-select": {
                py: 0.75,
              },
            }}
          >
            {rowsPerPageOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>
    </Box>
  );
};

export default PaginationControls;
